import * as Sentry from "@sentry/react";
import * as React from "react";

import { H3 } from "App/global-style";
import { CurrentPriceMessage } from "Components/CurrentPriceMessage/CurrentPriceMessage";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { MultipleProductCoverages } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/MultipleProductCoverages";
import { getCollectionOfDittoComponents } from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { CaravanCoverage } from "Store/interfaces/products/coverage";
import { useCoveragePriceCalculations } from "Store/priceCalculation/priceCalculationHooks";
import { ReduxState } from "Store/rootReducer";
import { useProductTexts } from "Store/texts/hooks";
import { isDefined } from "Utils/functions";
import { FormikProps } from "formik";
import { useSelector } from "react-redux";
import { DEFAULT_DEDUCTIBLE_CARAVAN } from "../constants";
import { CaravanTexts } from "../interface";
import { getDefaultCoverage } from "../utils";
import { Deductible } from "./Deductible";
import { CaravanProductPriceFormValues } from "./interfaces";

interface Props {
  readonly formProps: FormikProps<CaravanProductPriceFormValues>;
}

export const Coverages = (props: Props): React.ReactElement => {
  const { formProps } = props;
  const coverages = useCoveragePriceCalculations();
  const [texts, lang] = useProductTexts<CaravanTexts>(
    ProductBackendName.CARAVAN,
  );

  const { caravan: coverageCardTextComponents } =
    getCollectionOfDittoComponents(TextComponentNameLevel1.COVERAGE_CARD);
  const { isLoading } = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );

  React.useEffect(() => {
    if (isLoading || !isDefined(coverages) || coverages.length === 0) {
      return;
    }

    try {
      const defaultSelected = getDefaultCoverage(coverages);
      const isValidSelection = coverages
        .map((coverage) => coverage[0])
        .includes(formProps.values.coverage);

      if (!isValidSelection) {
        formProps.setFieldValue("coverage", defaultSelected);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [isLoading]);

  return (
    <>
      <Deductible
        formProps={formProps}
        coverages={coverages}
        texts={texts.formText.checkPrice}
        lang={lang}
      />

      <H3>{texts.formText.checkPrice.coverage.label[lang]}</H3>
      <MultipleProductCoverages
        fieldName="coverage"
        coverages={coverages}
        coverageStandards={Object.values(CaravanCoverage)}
        priceFilterValue={formProps.values.deductible}
        product={ProductBackendName.CARAVAN}
        priceIsLoading={isLoading}
        textComponents={coverageCardTextComponents}
      />
      <CurrentPriceMessage
        productBackendName={ProductBackendName.CARAVAN}
        coverages={coverages}
        selectedPrices={[
          {
            selectedCoverage: formProps.values.coverage,
            selectedDeductible: formProps.values.deductible,
            defaultDeductible: DEFAULT_DEDUCTIBLE_CARAVAN,
          },
        ]}
      />
    </>
  );
};
